import { BackLink } from "../../../app/components/BackLink.js";
import { Button, Card, StatusBadge, PageHeading } from "@peer-start/ui";
import { BadgeCheck, BookOpenCheck, CheckCircle2, Clock3, PlayCircle } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppContext } from "../../../app/use-app-context.js";
import { QuizPanel } from "../components/QuizPanel.js";
import { TrainingContent } from "../components/TrainingContent.js";

export function SkillPage() {
  const { t } = useTranslation();
  const { skillId } = useParams();
  const { bootstrap, persona, runCommand, commandPending } = useAppContext();
  const [assessmentOpen, setAssessmentOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  if (persona.kind !== "employee") return null;
  const skill = bootstrap.skills.find((entry) => entry.id === skillId);
  const progress = bootstrap.progress.find(
    (entry) => entry.employeeId === persona.id && entry.skillId === skillId,
  );
  const training = bootstrap.trainings.find((entry) => entry.id === skill?.trainingId);
  const role = bootstrap.roles.find((entry) => entry.id === persona.roleId);
  if (!skill || !progress || !training || !role?.skillIds.includes(skill.id))
    return <Card>Skill niet gevonden.</Card>;

  const act = async (command: Parameters<typeof runCommand>[0]) => {
    try {
      setNotice(await runCommand(command));
    } catch (error) {
      setNotice(error instanceof Error ? error.message : t("common.error"));
    }
  };
  const startAssessment = async () => {
    await act({ type: "assessment_started", employeeId: persona.id, skillId: skill.id });
    setAssessmentOpen(true);
  };
  const statusContent =
    progress.status === "unassessed" ? (
      assessmentOpen ? (
        <QuizPanel
          controlId={`assessment.${skill.id}`}
          onSubmit={async (answers) =>
            act({
              type: "assessment_completed",
              employeeId: persona.id,
              skillId: skill.id,
              answers,
            })
          }
          pending={commandPending}
          quiz={skill.assessment}
        />
      ) : (
        <Card>
          <PlayCircle aria-hidden="true" className="mb-4 size-10 text-primary" />
          <h2 className="text-xl font-medium">{t("skill.assessment")}</h2>
          <p className="mt-2 text-muted-foreground">
            Beantwoord {skill.assessment.questions.length} korte vraag. Je ziet meteen of je al door kunt.
          </p>
          <Button
            className="mt-6"
            controlId={`assessment.${skill.id}.start`}
            disabled={commandPending}
            onClick={() => void startAssessment()}
          >
            {t("skill.start")}
          </Button>
        </Card>
      )
    ) : progress.status === "to_train" ? (
      progress.trainingCompleted ? (
        <div className="grid gap-5 xl:grid-cols-2">
          <QuizPanel
            controlId={`test.${skill.id}`}
            onSubmit={async (answers) =>
              act({
                type: "test_completed",
                employeeId: persona.id,
                skillId: skill.id,
                answers,
              })
            }
            pending={commandPending}
            quiz={skill.test}
          />
          <TrainingContent controlId={`training.${skill.id}.review`} training={training} />
        </div>
      ) : progress.trainingStarted ? (
        <TrainingContent
          controlId={`training.${skill.id}`}
          onComplete={() =>
            void act({
              type: "training_completed",
              employeeId: persona.id,
              skillId: skill.id,
            })
          }
          pending={commandPending}
          training={training}
        />
      ) : (
        <Card>
          <BookOpenCheck aria-hidden="true" className="mb-4 size-10 text-primary" />
          <h2 className="text-xl font-medium">{training.title}</h2>
          <p className="mt-2 text-muted-foreground">{training.description}</p>
          <p className="mt-4 text-sm font-medium">{t("common.minutes", { count: training.minutes })}</p>
          <Button
            className="mt-6"
            controlId={`training.${skill.id}.start`}
            disabled={commandPending}
            onClick={() =>
              void act({
                type: "training_started",
                employeeId: persona.id,
                skillId: skill.id,
              })
            }
          >
            {t("skill.training")}
          </Button>
        </Card>
      )
    ) : progress.status === "on_checklist" ? (
      <Card className="bg-warning text-warning-foreground">
        <Clock3 aria-hidden="true" className="mb-4 size-10" />
        <h2 className="text-xl font-medium">{t("skill.waiting")}</h2>
        <p className="mt-2">{t("skill.waitingDescription")}</p>
      </Card>
    ) : (
      <Card className="bg-success text-success-foreground">
        <BadgeCheck aria-hidden="true" className="mb-4 size-10" />
        <h2 className="text-xl font-medium">{t("skill.established")}</h2>
        <p className="mt-2">Deze stempel blijft onderdeel van je vaardighedenregister.</p>
      </Card>
    );

  return (
    <>
      <BackLink controlId="skill.back" to="/me/passport">
        {t("common.back")}
      </BackLink>
      <PageHeading
        action={<StatusBadge label={t(`status.${progress.status}`)} status={progress.status} />}
        title={skill.title}
        subtitle={skill.description}
      />
      {notice ? (
        <div
          aria-live="polite"
          className="mb-5 flex items-center gap-2 rounded-xl bg-success px-4 py-3 font-medium text-success-foreground"
        >
          <CheckCircle2 aria-hidden="true" className="size-5" />
          {notice}
        </div>
      ) : null}
      {progress.supervisorFeedback ? (
        <Card className="mb-5 bg-warning text-warning-foreground">
          <h2 className="font-medium">{t("skill.feedback")}</h2>
          <p className="mt-2">“{progress.supervisorFeedback}”</p>
        </Card>
      ) : null}
      {statusContent}
    </>
  );
}
